import { useCallback, useState, useRef } from 'react';
import { HubConnectionBuilder } from '@microsoft/signalr';
import chargerActions from '../store/actions/chargerActions'
import { useDispatch, useSelector } from 'react-redux';
import { AlertService, AlertsIconEnum } from '../services/alerts.service';
import translationService from '../services/translator.service';
import { isNative } from '../platform/env';
import { secureGet } from '../platform/secureStore';
import { getNativeApiOrigin } from '../platform/apiOrigin';

// spec 000154 (S3.15, S3.T3.2) - pinned to the server's actual string, not a locally-invented copy.
// The server side of this pin is SignalROCPPClient.SendBillingCycleAccountUpdated's own literal
// ("BillingCycleAccountUpdated") - both sides are the SignalRMessageTypeEnum member name (=11) verbatim,
// same convention every existing case in this switch already follows.
export const BILLING_CYCLE_ACCOUNT_UPDATED_EVENT = 'BillingCycleAccountUpdated';

// Native WebViews are served from capacitor://localhost, so a relative hub
// URL (or the empty same-origin API base) can never reach the API server.
// When the configured value is relative on native, resolve it against the
// native API origin (spec 000211 Q6 - see platform/apiOrigin.js).

function useSignalR({ ...props }) {

    const [openPopupModal, setOpenPopupModal] = useState(false);
    const [, setSummaryData] = useState([]);
    const dispatch = useDispatch()
    const language = useSelector((state) => state.languageReducer.language);
    const { cookies } = props;
    // Only fall back to localhost when unset; an explicit empty value means same-origin.
    const configuredBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const apiBaseUrl = (configuredBaseUrl == null ? 'https://localhost:5011' : configuredBaseUrl).replace(/\/$/, '');
    let signalRHubUrl = process.env.REACT_APP_SIGNALR_HUB_URL || `${apiBaseUrl}/hubs/SignalRMobileClient`;
    if (isNative && !/^https?:\/\//i.test(signalRHubUrl)) {
        const nativeOrigin = getNativeApiOrigin();
        signalRHubUrl = `${nativeOrigin}${signalRHubUrl.startsWith('/') ? '' : '/'}${signalRHubUrl}`;
    }

    // Use ref to store the token to avoid recreating setupSignalRConnection when cookies change
    const tokenRef = useRef(null);
    const ourCookies = cookies.get('isLoggedIn');
    tokenRef.current = ourCookies?.token;

    const setupSignalRConnection = useCallback(() => {
        const hubUrl = signalRHubUrl;
        const newConnection = new HubConnectionBuilder()
            .withUrl(hubUrl, {
                // Token resolution order (re-read on every connect attempt):
                //   1. Native secure store (`authToken`)
                //   2. Cookie (`isLoggedIn.token`)
                // SignalR's accessTokenFactory accepts a Promise return.
                accessTokenFactory: async () => {
                    const fromSecure = await secureGet('authToken').catch(() => null);
                    return fromSecure || tokenRef.current || '';
                },
            })
            .withAutomaticReconnect()
            .configureLogging('info')
            .build();

        return newConnection;
    }, [signalRHubUrl]);

    const handleReceiveMessage = useCallback((eventName, message) => {
        // Call the corresponding event handler based on the event name
        switch (eventName) {
            case 'UpdateConnectorStatus':
                dispatch(chargerActions.updateConnectorStatus(message));
                break;
            case 'UpdateChargerStatus':
                dispatch(chargerActions.updateChargerStatus(message));
                break;
            case 'UpdateChargingSummery':
                setSummaryData([message]);
                dispatch(chargerActions.updateChargingSummery([message]));
                dispatch(chargerActions.clearTransactionData());
                break;
            case 'UpdateTransactionData':
                dispatch(chargerActions.updateTransactionData(message));
                break;
            case 'ChargePointDetails':
                dispatch(chargerActions.chargePointDetails(message));
                break;
            case BILLING_CYCLE_ACCOUNT_UPDATED_EVENT: {
                // spec 000154 (S3.15) - informational only: the current-cycle card (PaymentsScreen,
                // MonthlyCycleScreen) reads via its own fetch (useMyOpenCycles), not a Redux store this
                // event could update directly. A live-refresh wiring is a larger change than a single
                // Stage 3 task should make to that data flow; this at least tells the driver something
                // changed, in their own language, instead of silently doing nothing - matching the
                // enum's own doc comment that an unhandled value is dropped silently by design (this
                // one IS handled, just narrowly).
                const generalData = translationService.getTranslatedData(language, '');
                AlertService.showMessage(AlertsIconEnum.INFO, translationService.getTranslation(generalData, 'general.messages.billingCycleUpdated'));
                break;
            }
            default:
                break;
        }
    }, [dispatch, language]);

    const handlePopupModal = useCallback(() => {
        setOpenPopupModal(true);
    }, []);

    return { openPopupModal, setOpenPopupModal, setupSignalRConnection, handleReceiveMessage, handlePopupModal };
    // return { openPopupModal, setOpenPopupModal, summaryData, setupSignalRConnection, handleReceiveMessage, handlePopupModal };
}


export default useSignalR;